import { useMemo } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  Cell,
  ReferenceLine,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import type { PartyNationalResult } from '@/types'

interface SeatDistributionProps {
  partyResults: PartyNationalResult[]
  totalSeats: number
  className?: string
}

export function SeatDistribution({ partyResults, totalSeats, className }: SeatDistributionProps) {
  const majorityThreshold = Math.floor(totalSeats / 2) + 1

  const chartData = useMemo(
    () =>
      [...partyResults]
        .sort((a, b) => b.seats_won - a.seats_won)
        .filter((p) => p.seats_won > 0 || p.vote_share > 0),
    [partyResults]
  )

  return (
    <Card className={cn(className)}>
      <CardHeader>
        <CardTitle className="text-base">Seat Distribution</CardTitle>
        <CardDescription>
          {totalSeats} total seats · {majorityThreshold} needed for majority
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 0, right: 40, left: 0, bottom: 0 }}
            >
              <XAxis type="number" domain={[0, 'auto']} />
              <YAxis
                type="category"
                dataKey="party_abbreviation"
                width={60}
                tick={{ fontSize: 12 }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const item = payload[0].payload as PartyNationalResult
                    return (
                      <div className="bg-popover text-popover-foreground rounded-lg border p-2 shadow-md">
                        <div className="font-medium">{item.party_name}</div>
                        <div className="text-muted-foreground text-sm">
                          {item.seats_won} seats ({item.seat_share.toFixed(1)}% of seats)
                        </div>
                        <div className="text-muted-foreground text-xs">
                          {item.vote_share.toFixed(1)}% vote share
                        </div>
                      </div>
                    )
                  }
                  return null
                }}
              />
              <ReferenceLine
                x={majorityThreshold}
                stroke="#ef4444"
                strokeDasharray="3 3"
                label={{ value: 'Majority', position: 'top', fontSize: 11, fill: '#ef4444' }}
              />
              <Bar dataKey="seats_won" radius={[0, 4, 4, 0]}>
                {chartData.map((entry) => (
                  <Cell key={entry.party_id} fill={entry.party_color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Legend with seat counts */}
        <div className="mt-4 flex flex-wrap gap-3">
          {chartData.map((p) => (
            <div key={p.party_id} className="flex items-center gap-1.5 text-sm">
              <div
                className="h-3 w-3 rounded-sm"
                style={{ backgroundColor: p.party_color }}
              />
              <span>{p.party_abbreviation}</span>
              <span className="text-muted-foreground tabular-nums">{p.seats_won}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
